import type { Arealplan } from '@origo/arealplan';
import type { FilterOption, PlanFilters, PlanPublishStatus, SortOption, SectionFilterValue } from './types';
import type { PlanMetadata } from '../admin/services/PlanMetadataService';

export function getPlanPublishStatus(meta: PlanMetadata | undefined): PlanPublishStatus {
    if (meta?.publisert) return 'publisert';
    if ((meta?.seksjonCount ?? 0) > 0) return 'utkast';
    return 'ikke-påbegynt';
}

/**
 * Hent unike filterverdier for et felt på Arealplan, talt opp.
 * Foretrekker `displayValues` (CodedValue-resolverte etiketter) når tilgjengelig,
 * fallback til rå domain-felt.
 */
export function getFilterOptions(plans: Arealplan[], field: keyof Arealplan): FilterOption[] {
    const counts = new Map<string, number>();
    for (const plan of plans) {
        const display = plan.displayValues?.[field as string];
        const raw = plan[field];
        const val = display ?? raw;
        if (val != null && val !== '') {
            const key = String(val);
            counts.set(key, (counts.get(key) ?? 0) + 1);
        }
    }
    return [...counts.entries()]
        .map(([value, count]) => ({ value, count }))
        .sort((a, b) => a.value.localeCompare(b.value, 'nb'));
}

export function getIkraftYearOptions(plans: Arealplan[]): FilterOption[] {
    const counts = new Map<string, number>();
    let withCount = 0;
    let withoutCount = 0;
    for (const plan of plans) {
        const ts = plan.ikraft;
        if (ts != null) {
            withCount++;
            const year = new Date(ts).getFullYear().toString();
            counts.set(year, (counts.get(year) ?? 0) + 1);
        } else {
            withoutCount++;
        }
    }
    return [
        { value: 'with', count: withCount },
        { value: 'without', count: withoutCount },
        ...[...counts.entries()]
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.value.localeCompare(a.value)),
    ];
}

export function hasActiveFilters(filters: PlanFilters): boolean {
    return (
        filters.statuses.length > 0 ||
        filters.types.length > 0 ||
        filters.vertikalNivaer.length > 0 ||
        filters.innholdsStatuser.length > 0 ||
        filters.dateFrom !== null ||
        filters.dateTo !== null ||
        filters.sectionFilter !== 'all'
    );
}

export function countActiveFilters(filters: PlanFilters): number {
    let count = filters.statuses.length + filters.types.length + filters.vertikalNivaer.length + filters.innholdsStatuser.length;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.sectionFilter !== 'all') count++;
    return count;
}

export function applyFilters(
    plans: Arealplan[],
    filters: PlanFilters,
    sectionCounts?: Map<string, number>,
    planMetadata?: Map<string, PlanMetadata>
): Arealplan[] {
    if (!hasActiveFilters(filters)) return plans;

    return plans
        .filter((plan) => matchesStatus(plan, filters.statuses))
        .filter((plan) => matchesType(plan, filters.types))
        .filter((plan) => matchesVertikalNiva(plan, filters.vertikalNivaer))
        .filter((plan) => matchesInnholdsStatus(plan, filters.innholdsStatuser, planMetadata))
        .filter((plan) => matchesDateRange(plan, filters.dateFrom, filters.dateTo))
        .filter((plan) => matchesSectionFilter(plan, filters.sectionFilter, sectionCounts));
}

function matchesInnholdsStatus(
    plan: Arealplan,
    innholdsStatuser: PlanPublishStatus[],
    planMetadata?: Map<string, PlanMetadata>
): boolean {
    if (innholdsStatuser.length === 0) return true;
    const meta = planMetadata?.get(plan.planId);
    return innholdsStatuser.includes(getPlanPublishStatus(meta));
}

function matchesStatus(plan: Arealplan, statuses: string[]): boolean {
    if (statuses.length === 0) return true;
    const status = String(plan.displayValues?.planStat ?? plan.planStat ?? '');
    return statuses.includes(status);
}

function matchesType(plan: Arealplan, types: string[]): boolean {
    if (types.length === 0) return true;
    // planTypeKey er internt nøkkel ("Reguleringsplan_VN3"); displayValues.planTypeKey
    // har den menneskelige etiketten ("Reguleringsplan").
    const type = String(plan.displayValues?.planTypeKey ?? plan.planTypeKey);
    return types.includes(type);
}

function matchesVertikalNiva(plan: Arealplan, nivaer: string[]): boolean {
    if (nivaer.length === 0) return true;
    const niva = String(plan.displayValues?.vertNiv ?? plan.vertNiv ?? '');
    return nivaer.includes(niva);
}

function matchesSectionFilter(
    plan: Arealplan,
    sectionFilter: SectionFilterValue,
    sectionCounts?: Map<string, number>
): boolean {
    if (sectionFilter === 'all') return true;
    const count = sectionCounts?.get(plan.planId) ?? 0;
    return sectionFilter === 'with-sections' ? count > 0 : count === 0;
}

function matchesDateRange(plan: Arealplan, dateFrom: Date | null, dateTo: Date | null): boolean {
    if (!dateFrom && !dateTo) return true;
    const ikraft = plan.ikraft;
    if (ikraft == null) return false;
    const ikraftTime = new Date(ikraft).getTime();
    if (dateFrom && ikraftTime < dateFrom.getTime()) return false;
    if (dateTo) {
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        if (ikraftTime > endOfDay.getTime()) return false;
    }
    return true;
}

export function sortPlans(plans: Arealplan[], sort: SortOption): Arealplan[] {
    const sorted = [...plans];
    const dir = sort.direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
        const aVal = a[sort.field] ?? '';
        const bVal = b[sort.field] ?? '';
        if (sort.field === 'ikraft') {
            const aTime = aVal ? new Date(aVal as string).getTime() : 0;
            const bTime = bVal ? new Date(bVal as string).getTime() : 0;
            return (aTime - bTime) * dir;
        }
        return String(aVal).localeCompare(String(bVal), 'nb') * dir;
    });
    return sorted;
}

export function formatDate(timestamp: number | string | null | undefined): string {
    if (timestamp == null) return '';
    try {
        return new Date(timestamp).toLocaleDateString('nb-NO', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
        return '';
    }
}

export function formatDateLong(timestamp: number | string | null | undefined): string {
    if (timestamp == null) return '';
    try {
        return new Date(timestamp).toLocaleDateString('nb-NO', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
        return '';
    }
}

export function monthsAgo(months: number): Date {
    const d = new Date();
    d.setMonth(d.getMonth() - months);
    d.setHours(0, 0, 0, 0);
    return d;
}
